import {
	AssetTypeCode,
	ComplexityLevel,
	HookCode,
	HookStage,
	TransitionDirection,
	type WorkflowJsonObject,
	type WorkflowJsonValue,
	WorkflowPhase,
	WORKFLOW_STATUS_CODE_PATTERN,
	WorkspaceCode,
} from '@atlas/types'
import { z } from 'zod'

// ─── Module-private scalar validators ──────────────────────────────────────
// Not exported from this file or from `@atlas/models`.

const workflowUuidSchema = z.string().uuid()

const workflowTextSchema = z.string().min(1)

const workflowNonNegativeIntegerSchema = z.number().finite().int().min(0)

/** A builder canvas coordinate — `null` clears a saved position, `undefined`/omitted leaves it untouched. */
const workflowCanvasCoordinateSchema = z.number().finite().int().nullable().optional()

/**
 * A workflow status `code` is an identifier, not display text: it is the value
 * a transition request names as its destination, so it must satisfy the same
 * canonical format the transition boundary enforces. Human-readable wording
 * belongs in `label`, which stays unrestricted.
 */
const workflowStatusCodeSchema = z
	.string()
	.min(1)
	.regex(WORKFLOW_STATUS_CODE_PATTERN, 'code must be lowercase ASCII letters, digits, or underscores')

// ─── Module-private generic JSON-object validation ─────────────────────────
// `params` and `options` validate as a JSON-compatible object, never
// unrestricted `unknown`/`any`. Per-hook parameter schemas belong with the
// hook handlers that consume them, not with this foundation.

/**
 * True only for a non-null, non-array object whose prototype is exactly
 * `Object.prototype` or `null` — rejects `Date`, `Map`, `Set`, `RegExp`,
 * `Error`, `URL`, and custom-class instances, including ones with no
 * enumerable properties. Runs before any Zod transform, clone, or
 * normalization of the candidate object.
 */
function isPlainWorkflowJsonObject(value: unknown): value is Record<string, unknown> {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
	const proto: unknown = Object.getPrototypeOf(value)
	return proto === Object.prototype || proto === null
}

const workflowJsonObjectSchema: z.ZodType<WorkflowJsonObject> = z
	.custom<Record<string, unknown>>(isPlainWorkflowJsonObject, { message: 'Expected a plain JSON object' })
	.pipe(
		z.record(
			z.string(),
			z.lazy(() => workflowJsonValueSchema),
		),
	)

const workflowJsonValueSchema: z.ZodType<WorkflowJsonValue> = z.lazy(() =>
	z.union([
		z.string(),
		z.number().finite(),
		z.boolean(),
		z.null(),
		z.array(workflowJsonValueSchema),
		workflowJsonObjectSchema,
	]),
)

// ─── Module-private resolver matcher validators ────────────────────────────

const resolverAssetTypeCodesSchema = z.array(z.enum(AssetTypeCode)).min(1).nullable()
const resolverComplexityLevelsSchema = z.array(z.enum(ComplexityLevel)).min(1).nullable()

/**
 * A resolver rule with both matchers `null` would duplicate the tenant
 * default's fallback semantics, so it is rejected wherever the
 * complete cross-field state is known — the create schema and the reusable
 * complete-state schema. The partial-update schema cannot apply this rule:
 * a patch alone never carries enough information to know the resulting
 * complete state.
 */
function rejectBothResolverMatchersNull(
	value: { assetTypeCodes: AssetTypeCode[] | null; complexityLevels: ComplexityLevel[] | null },
	ctx: z.RefinementCtx,
): void {
	if (value.assetTypeCodes === null && value.complexityLevels === null) {
		ctx.addIssue({
			code: 'custom',
			message: 'At least one of assetTypeCodes or complexityLevels must be constrained',
			path: ['assetTypeCodes'],
		})
	}
}

// ─── Module-private partial-update refinement ──────────────────────────────

/**
 * Rejects an explicit `undefined` value on any own key (omission is the only
 * way to leave a field unset) and rejects a patch with zero own keys whose
 * value is not `undefined`. Applied to every update schema in this module —
 * never transforms, strips, defaults, or replaces the parsed object.
 */
function validateWorkflowPatchObject(value: Record<string, unknown>, ctx: z.RefinementCtx): void {
	let definedCount = 0
	for (const key of Object.keys(value)) {
		if (value[key] === undefined) {
			ctx.addIssue({
				code: 'custom',
				message: 'Explicit undefined is not allowed — omit the property instead',
				path: [key],
			})
		} else {
			definedCount += 1
		}
	}
	if (definedCount === 0) {
		ctx.addIssue({ code: 'custom', message: 'At least one field must be supplied' })
	}
}

// ─── workflow ───────────────────────────────────────────────────────────────

/**
 * Create a workflow. `code` is the immutable logical key. A created workflow
 * starts inactive and non-default, so `isActive` and `isDefault` are not
 * accepted here — activation and default designation are dedicated operations
 * with their own preconditions.
 */
export const workflowCreateSchema = z
	.object({
		code: workflowTextSchema,
		name: workflowTextSchema,
	})
	.strict()

/**
 * Update a workflow's mutable metadata. `code` is immutable after creation.
 * The lifecycle flags are deliberately absent: activation, deactivation,
 * default replacement, and soft deletion each have preconditions that a
 * generic patch would bypass, so they are separate operations and this schema
 * rejects `isActive`, `isDefault`, and `deletedAt` as unknown fields.
 */
export const workflowUpdateSchema = z
	.object({
		name: workflowTextSchema.optional(),
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)

/**
 * Activate a workflow. `setAsDefault` designates it as the tenant default in
 * the same transaction — required when the tenant has no active default, and
 * optional otherwise.
 */
export const workflowActivateSchema = z
	.object({
		setAsDefault: z.boolean().default(false),
	})
	.strict()

/**
 * Deactivate or soft-delete a workflow. `replacementWorkflowId` names the
 * workflow that takes over as tenant default in the same transaction, and is
 * required only when the target is the current default.
 */
export const workflowLifecycleReplacementSchema = z
	.object({
		replacementWorkflowId: workflowUuidSchema.optional(),
	})
	.strict()

// ─── workflow_status ────────────────────────────────────────────────────────

export const workflowStatusCreateSchema = z
	.object({
		workflowId: workflowUuidSchema,
		code: workflowStatusCodeSchema,
		label: workflowTextSchema,
		phase: z.enum(WorkflowPhase),
		position: workflowNonNegativeIntegerSchema,
		workspaceDefault: z.enum(WorkspaceCode),
		isInitial: z.boolean().default(false),
		isTerminal: z.boolean().default(false),
		canvasX: workflowCanvasCoordinateSchema,
		canvasY: workflowCanvasCoordinateSchema,
	})
	.strict()

/** `workflowId` is the create-time ownership relationship and is immutable — never accepted here. */
export const workflowStatusUpdateSchema = z
	.object({
		code: workflowStatusCodeSchema.optional(),
		label: workflowTextSchema.optional(),
		phase: z.enum(WorkflowPhase).optional(),
		position: workflowNonNegativeIntegerSchema.optional(),
		workspaceDefault: z.enum(WorkspaceCode).optional(),
		isInitial: z.boolean().optional(),
		isTerminal: z.boolean().optional(),
		isActive: z.boolean().optional(),
		canvasX: workflowCanvasCoordinateSchema,
		canvasY: workflowCanvasCoordinateSchema,
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)

/**
 * Reorder one workflow's active statuses. `statusIds` is the complete, ordered
 * list of that workflow's active statuses, renumbered densely from zero;
 * duplicates are rejected because a status cannot hold two positions.
 *
 * A status's position is not settable through `workflowStatusUpdateSchema`'s
 * `position` field - the administration service rejects that patch and points
 * the caller here, so sequence is only ever expressed as a whole order.
 */
export const workflowStatusReorderSchema = z
	.object({
		workflowId: workflowUuidSchema,
		statusIds: z
			.array(workflowUuidSchema)
			.min(1)
			.refine((ids) => new Set(ids).size === ids.length, { message: 'statusIds must not contain duplicates' }),
	})
	.strict()

// ─── workflow_transition ────────────────────────────────────────────────────

export const workflowTransitionCreateSchema = z
	.object({
		workflowId: workflowUuidSchema,
		fromStatusId: workflowUuidSchema,
		toStatusId: workflowUuidSchema,
		ctaLabel: workflowTextSchema,
		direction: z.enum(TransitionDirection).default(TransitionDirection.FORWARD),
		position: workflowNonNegativeIntegerSchema.default(0),
	})
	.strict()

/** `workflowId` is the create-time ownership relationship and is immutable — never accepted here. */
export const workflowTransitionUpdateSchema = z
	.object({
		fromStatusId: workflowUuidSchema.optional(),
		toStatusId: workflowUuidSchema.optional(),
		ctaLabel: workflowTextSchema.optional(),
		direction: z.enum(TransitionDirection).optional(),
		position: workflowNonNegativeIntegerSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)

// ─── transition_hook ────────────────────────────────────────────────────────
// `params` validates as a generic JSON object at this layer; each code's own
// shape is enforced by `hookParamsSchemaByCode` below, at the admin write
// boundary, so a work order never reaches execution against invalid params.

/** Every `HookCode`'s home stage — the transition engine (PR2) owns deciding which codes are guards (PRE) vs. actions (POST); see `workflow.enums.ts`'s own note that `HookStage`/`HookCode` deliberately don't encode this pairing themselves. */
export const HOOK_STAGE_BY_CODE: Record<HookCode, HookStage> = {
	[HookCode.CHECKLIST_COMPLETE]: HookStage.PRE,
	[HookCode.DELIVERY_SLOTS_FILLED]: HookStage.PRE,
	[HookCode.ARTIFACT_EXISTS]: HookStage.PRE,
	[HookCode.CHANGE_REQUEST_CREATED]: HookStage.PRE,
	[HookCode.CHANGE_REQUEST_RESOLVED]: HookStage.PRE,
	[HookCode.ASSIGNEE_PRESENT]: HookStage.PRE,
	[HookCode.FIELDS_PRESENT]: HookStage.PRE,
	[HookCode.EMIT_EVENT]: HookStage.POST,
	[HookCode.NOTIFY]: HookStage.POST,
	[HookCode.LOG_MESSAGE]: HookStage.POST,
}

/** A write pairing a `HookCode` with the wrong `HookStage` is rejected — only when both fields are present on this payload; a partial update supplying just one side is checked against the persisted hook's other field at the service layer instead (mirrors how `hookParamsSchemaByCode` validation is split the same way). */
function rejectMismatchedHookStage(value: { stage?: HookStage; hookCode?: HookCode }, ctx: z.RefinementCtx): void {
	if (value.stage === undefined || value.hookCode === undefined) return
	const expected = HOOK_STAGE_BY_CODE[value.hookCode]
	if (expected !== value.stage) {
		ctx.addIssue({
			code: 'custom',
			message: `'${value.hookCode}' is a ${expected}-stage hook, not '${value.stage}'`,
			path: ['stage'],
		})
	}
}

export const transitionHookCreateSchema = z
	.object({
		transitionId: workflowUuidSchema,
		stage: z.enum(HookStage),
		hookCode: z.enum(HookCode),
		params: workflowJsonObjectSchema.default({}),
		position: workflowNonNegativeIntegerSchema.default(0),
	})
	.strict()
	.superRefine(rejectMismatchedHookStage)

/** `transitionId` is the create-time ownership relationship and is immutable — never accepted here. */
export const transitionHookUpdateSchema = z
	.object({
		stage: z.enum(HookStage).optional(),
		hookCode: z.enum(HookCode).optional(),
		params: workflowJsonObjectSchema.optional(),
		position: workflowNonNegativeIntegerSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)
	.superRefine(rejectMismatchedHookStage)

// ─── workflow_resolver_rule ─────────────────────────────────────────────────
// Storage and matcher semantics. Resolution itself, priority ordering, and
// the default-workflow fallback are enforced by the resolver and the
// administration operations, not by these schemas.

export const workflowResolverRuleCreateSchema = z
	.object({
		workflowId: workflowUuidSchema,
		priority: workflowNonNegativeIntegerSchema,
		assetTypeCodes: resolverAssetTypeCodesSchema,
		complexityLevels: resolverComplexityLevelsSchema,
	})
	.strict()
	.superRefine(rejectBothResolverMatchersNull)

/**
 * Shape-only validation. A partial payload cannot determine the resulting
 * complete cross-field state, so no both-null refinement is applied here —
 * the future resolver-rule update operation must merge this patch with the
 * persisted rule and validate the result through
 * {@link workflowResolverRuleCompleteStateSchema}. `workflowId` is a matcher
 * outcome rather than an ownership relationship — retargeting a rule at
 * another workflow is an ordinary edit — so it is accepted here and validated
 * against the acting tenant's workflows by the update operation. `tenantId` is
 * never accepted from a client.
 */
export const workflowResolverRuleUpdateSchema = z
	.object({
		workflowId: workflowUuidSchema.optional(),
		priority: workflowNonNegativeIntegerSchema.optional(),
		assetTypeCodes: resolverAssetTypeCodesSchema.optional(),
		complexityLevels: resolverComplexityLevelsSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)

/**
 * Validates the complete resolver-matcher cross-field state — exactly
 * `assetTypeCodes` and `complexityLevels`, both required. The resolver-rule
 * update operation constructs this schema's input by merging the persisted
 * rule with the validated patch, and validates the merged result before
 * writing — so a patch that is individually valid but leaves the rule
 * catch-all is rejected.
 */
export const workflowResolverRuleCompleteStateSchema = z
	.object({
		assetTypeCodes: resolverAssetTypeCodesSchema,
		complexityLevels: resolverComplexityLevelsSchema,
	})
	.strict()
	.superRefine(rejectBothResolverMatchersNull)

/**
 * Reorder a tenant's active resolver rules. `ruleIds` is the complete, ordered
 * list of the rules to renumber densely from the lowest priority upward;
 * duplicates are rejected because a rule cannot hold two positions.
 */
export const workflowResolverRuleReorderSchema = z
	.object({
		ruleIds: z
			.array(workflowUuidSchema)
			.min(1)
			.refine((ids) => new Set(ids).size === ids.length, { message: 'ruleIds must not contain duplicates' }),
	})
	.strict()

// ─── workflow repair ────────────────────────────────────────────────────────

/**
 * Repair acts on an explicitly named set of work orders, never on "everything
 * broken" — an unbounded tenant-wide mutation is not something an
 * administrator can review before running. `dryRun` is required rather than
 * defaulted: which mode you are in is the single most consequential thing
 * about the request, and it should be visible in the payload.
 *
 * Duplicates are rejected because a work order cannot have two outcomes in one
 * report, and a repeated id would otherwise be silently processed twice.
 */
export const workflowRepairSchema = z
	.object({
		workOrderIds: z
			.array(workflowUuidSchema)
			.min(1)
			.max(200)
			.refine((ids) => new Set(ids).size === ids.length, { message: 'workOrderIds must not contain duplicates' }),
		dryRun: z.boolean(),
	})
	.strict()

// ─── Inferred input types ───────────────────────────────────────────────────
// The administration service and controllers type their payloads from these
// rather than restating the shapes.

export type WorkflowCreateInput = z.infer<typeof workflowCreateSchema>
export type WorkflowUpdateInput = z.infer<typeof workflowUpdateSchema>
export type WorkflowActivateInput = z.infer<typeof workflowActivateSchema>
export type WorkflowLifecycleReplacementInput = z.infer<typeof workflowLifecycleReplacementSchema>
export type WorkflowStatusCreateInput = z.infer<typeof workflowStatusCreateSchema>
export type WorkflowStatusUpdateInput = z.infer<typeof workflowStatusUpdateSchema>
export type WorkflowStatusReorderInput = z.infer<typeof workflowStatusReorderSchema>
export type WorkflowTransitionCreateInput = z.infer<typeof workflowTransitionCreateSchema>
export type WorkflowTransitionUpdateInput = z.infer<typeof workflowTransitionUpdateSchema>
export type TransitionHookCreateInput = z.infer<typeof transitionHookCreateSchema>
export type TransitionHookUpdateInput = z.infer<typeof transitionHookUpdateSchema>
export type WorkflowResolverRuleCreateInput = z.infer<typeof workflowResolverRuleCreateSchema>
export type WorkflowResolverRuleUpdateInput = z.infer<typeof workflowResolverRuleUpdateSchema>
export type WorkflowResolverRuleReorderInput = z.infer<typeof workflowResolverRuleReorderSchema>
export type WorkflowRepairInput = z.infer<typeof workflowRepairSchema>

// ─── workspace_assignment ───────────────────────────────────────────────────

export const workspaceAssignmentCreateSchema = z
	.object({
		workflowId: workflowUuidSchema,
		statusId: workflowUuidSchema,
		assetTypeCode: z.enum(AssetTypeCode).nullable(),
		workspaceCode: z.enum(WorkspaceCode),
		options: workflowJsonObjectSchema.default({}),
	})
	.strict()

/** `workflowId` and `statusId` are the create-time ownership relationships and are immutable — never accepted here. */
export const workspaceAssignmentUpdateSchema = z
	.object({
		assetTypeCode: z.enum(AssetTypeCode).nullable().optional(),
		workspaceCode: z.enum(WorkspaceCode).optional(),
		options: workflowJsonObjectSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateWorkflowPatchObject)

export type WorkspaceAssignmentCreateInput = z.infer<typeof workspaceAssignmentCreateSchema>
export type WorkspaceAssignmentUpdateInput = z.infer<typeof workspaceAssignmentUpdateSchema>
